/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

// Package Imports
import React, { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { Rating as ReactRating, StickerStar } from '@smastrom/react-rating';
import classNames from 'classnames';

// Components
import Modal from '@/components/modal';
import { Reviews } from '../reviews';
import { SubmitReview } from '../submitReview';
import { ProductReviews } from '@/lib/types/productReviews';
import ModalLogin from '@/components/modalLogin';
import ModalRegistration from '@/components/modalRegistration';

// Functions
import { getProductReviews } from '@/lib/functions/getProductReviews';

// Styles
import '@smastrom/react-rating/style.css';
import styles from './productRatings.module.scss';

// Define Interfaces
interface ProductRatingsProps {
  averageRating?: number;
  reviewsCount: number;
  slug: string;
  sku: string;
  submitReviewSuccess: boolean;
  setSubmitReviewSuccess: (val: any) => void;
}

/**
 * ProductRatings Component
 *
 * This component renders the average rating of a product and provides a link to view reviews.
 *
 * Props:
 * - averageRating: The average rating of the product.
 * - reviews: An array of reviews for the product.
 *
 * State:
 * - rating: Manages the current rating value.
 * - isReviewsModalOpen: Controls the modal state.
 *
 * Usage:
 * The component uses `ReactRating` to display the average rating and a `Modal` to show the reviews.
 */
export const ProductRatings: React.FC<ProductRatingsProps> = ({
  averageRating = 0,
  reviewsCount = 0,
  slug = '',
  sku = '',
  submitReviewSuccess,
  setSubmitReviewSuccess,
}) => {
  const [rating, setRating] = useState<number>(averageRating);
  const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);
  const [isSubmitReviewModalOpen, setIsSubmitReviewModalOpen] = useState(false);
  const [reviewsData, setReviewsData] = useState<ProductReviews>({
    product: {
      id: 0,
      name: '',
      slug: '',
    },
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    reviews: [],
    hasReviewed: false,
  });
  const [isReviewsLoading, setIsReviewsLoading] = useState(true);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

  const searchParams = useSearchParams();
  const pageParam = searchParams.get('page') || '1';
  const page = parseInt(pageParam, 10) || 1;

  const { status, data: session } = useSession();

  const userId = session?.user?.customerId;

  const ratingStyles = {
    itemShapes: StickerStar,
    activeFillColor: '#dc181f',
    inactiveFillColor: '#ffffff',
    activeStrokeColor: '#dc181f',
    inactiveStrokeColor: '#dc181f',
    itemStrokeWidth: 1,
  };

  const handleRating = (value: number) => {
    setRating(value);
  };

  useEffect(() => {
    const fetchReviews = async () => {
      setIsReviewsLoading(true);
      try {
        const data = await getProductReviews(slug, page, userId);
        setReviewsData(data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setIsReviewsLoading(false);
      }
    };

    // prevent multiple API calls while things are still loading
    if (slug && page && status !== 'loading') {
      fetchReviews();
    }
  }, [slug, page, userId, status]);

  return (
    <>
      <div className={styles.wrapper}>
        <ReactRating
          className={styles.wrapper}
          value={rating}
          onChange={handleRating}
          readOnly={true}
          itemStyles={ratingStyles}
        />
      </div>
      <div className={styles.actionLinks}>
        {/* Reviews Modal */}
        {status !== 'authenticated' || isReviewsLoading ? (
          <>
            {/* Open Login Modal */}
            <button className={styles.likeALink} onClick={() => setIsLoginModalOpen(true)}>
              Log In
            </button>{' '}
            or {/* Open Registration Modal */}
            <button className={styles.likeALink} onClick={() => setIsRegisterModalOpen(true)}>
              Register
            </button>{' '}
            to Leave a Review |{' '}
          </>
        ) : (
          !reviewsData.hasReviewed &&
          !submitReviewSuccess && (
            <>
              <Modal
                isOpen={isSubmitReviewModalOpen}
                onClose={() => setIsSubmitReviewModalOpen(false)}
                content={
                  <SubmitReview
                    onClose={() => setIsSubmitReviewModalOpen(false)}
                    productCode={sku}
                    onSuccess={() => setSubmitReviewSuccess(true)}
                  />
                }
                modalClass={styles.productReviewsModal}
              />
              <button className={classNames(styles.likeALink)} onClick={() => setIsSubmitReviewModalOpen(true)}>
                Leave a Review
              </button>{' '}
              |{' '}
            </>
          )
        )}

        <Modal
          isOpen={isReviewsModalOpen}
          onClose={() => setIsReviewsModalOpen(false)}
          modalClass={styles.productReviewsModal}
          content={<Reviews reviewsData={reviewsData} isLoading={isReviewsLoading} page={page} />}
        />
        <button
          className={classNames(styles.likeALink, { [styles.disabled]: reviewsCount <= 0 })}
          onClick={() => {
            if (reviewsCount > 0) {
              setIsReviewsModalOpen(true);
            }
          }}
          disabled={reviewsCount <= 0}
        >
          {`${reviewsCount} Review${reviewsCount == 1 ? '' : 's'}`}
        </button>

        {/* Login & Registration Modals */}
        <ModalLogin isOpen={isLoginModalOpen} setIsOpen={setIsLoginModalOpen} />
        <ModalRegistration isOpen={isRegisterModalOpen} setIsOpen={setIsRegisterModalOpen} />
      </div>
      {reviewsData.reviews.slice(0, 3).map((data, index) => (
        <script
          key={`structured-review-${index}`}
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Review',
              author: {
                '@type': 'Person',
                name: data.author?.name || 'Anonymous',
              },
              datePublished: new Date(data.createdAt).toISOString(),
              reviewBody: data.comment,
              reviewRating: {
                '@type': 'Rating',
                ratingValue: data.rating,
                bestRating: '5',
              },
            }),
          }}
        />
      ))}
    </>
  );
};
